/**
 * The group editor's words for the permission ids (D14; `PERMISSION_IDS` in
 * `@acp/api-contract/permissions.js`, the one list). Every id has a label and
 * a one-line description — `admin.test.ts` fails when an id is added without
 * one — and a group in a customer's tenant is offered only the grantable ones
 * (`vendor.*` only in the vendor's own tenant, which the server enforces too).
 */
import { isVendorPermission, PERMISSION_IDS, type PermissionId } from '@acp/api-contract/permissions.js';
import { protectedPermissions } from '@acp/api-contract/tenancy.js';

export type PermissionInfo = { readonly id: PermissionId; readonly label: string; readonly description: string };

export const PERMISSION_LABELS: Readonly<Record<PermissionId, { readonly label: string; readonly description: string }>> = {
  'tenant.admin': { label: 'Administer the tenant', description: 'Users, groups, sites, nodes, device secrets, the audit log. Counts only when granted tenant-wide.' },
  'billing.view': { label: 'See usage and cost', description: 'Cloud production hours and their estimated cost.' },
  'rundown.read': { label: 'Read rundowns', description: 'Open rundowns and playlists.' },
  'rundown.edit': { label: 'Edit rundowns', description: 'Create, edit, reorder and delete rundowns and playlists.' },
  'playout.take': { label: 'Take and set next', description: 'Take, set next, adlib; confirm on-air updates.' },
  'playout.activate': { label: 'Activate playlists', description: 'Activate, deactivate and reset a playlist.' },
  'production.launch.onprem': { label: 'Launch on-prem productions', description: 'Start a production on a studio’s nodes.' },
  'production.launch.cloud': { label: 'Launch cloud productions', description: 'Start a production in the cloud (it costs money while it runs).' },
  'node.switchover': { label: 'Switch main and backup', description: 'Move a production, or a whole node, to the other node of its pair.' },
  'device.configure': { label: 'Configure studios', description: 'Devices, mappings, show styles.' },
  'local.login': { label: 'Offline sign-in', description: 'Sign in on a node with an offline PIN while it cannot reach the cloud.' },
  'vendor.ops': { label: 'Vendor operations', description: 'The cross-tenant operations dashboard. Vendor tenant only.' },
  'vendor.admin': { label: 'Vendor administration', description: 'Create and manage tenants. Vendor tenant only.' },
};

/** The permissions a group in this tenant may hold, in the contract's order. */
export function grantablePermissions(isVendorTenant: boolean): readonly PermissionInfo[] {
  return PERMISSION_IDS.filter((id) => isVendorTenant || !isVendorPermission(id)).map((id) => ({ id, ...PERMISSION_LABELS[id] }));
}

/**
 * The permissions a protected group keeps (the Admin group's admin rights): the contract's one list, which the
 * server's fence reads too; null for any other group. A protected group also stays tenant-wide and cannot be deleted.
 */
export function lockedPermissions(groupId: string): readonly PermissionId[] | null {
  return protectedPermissions(groupId);
}

/** A permission id's label, for lists. */
export function permissionLabel(id: PermissionId): string {
  return PERMISSION_LABELS[id].label;
}
